import {
    setMergedGeometry,
    setAreaFinalMultiplePlot
} from "../services/reducers/multiple-plot";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import {
    excludedObjectIds, getExpression,
    getOriginalRenderer,
    setRenderer
} from "./helpers-for-2d-map";
import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";

let cursorIconInstance = null;
let pointerMoveHandler = null

export const updateMergedGraphics = async (groupedFeatures, tempLineLayer, map, dispatch) => {
    tempLineLayer.removeAll();
    await projection.load();

    const mergedFeatures = [...groupedFeatures.initial, ...groupedFeatures.toMerge];

    if (groupedFeatures.toMerge.length > 0) {
        const mergedGeometry = geometryEngine.union(mergedFeatures.map(f => f.geometry));

        dispatch(setMergedGeometry(mergedGeometry));

        const newSR = new SpatialReference({
            wkid: 3857
        })
        const projectedGeometry = projection.project(mergedGeometry, newSR);
        const area = Math.round(geometryEngine.geodesicArea(projectedGeometry, "square-meters"))

        dispatch(setAreaFinalMultiplePlot(area))

        const outlineSymbol = new SimpleLineSymbol({
            color: '#2E1A47',
            width: 3
        });

        const outlineGraphic = new Graphic({
            geometry: mergedGeometry,
            symbol: outlineSymbol
        });

        tempLineLayer.add(outlineGraphic);

        const vertices = mergedGeometry.rings.flat();
        const seenPoints = new Set();

        vertices.forEach((vertex, index) => {
            const key = `${vertex[0]}-${vertex[1]}`;

            if (!seenPoints.has(key)) {
                seenPoints.add(key);

                const point = new Point({
                    x: vertex[0],
                    y: vertex[1],
                    spatialReference: mergedGeometry.spatialReference
                });

                const pointSymbol = new SimpleMarkerSymbol({
                    color: '#2E1A47',
                    size: 10,
                    outline: {
                        color: '#FFFFFF',
                        width: 3
                    }
                });

                const pointGraphic = new Graphic({
                    geometry: point,
                    symbol: pointSymbol
                });

                tempLineLayer.add(pointGraphic);

                const textSymbol = new TextSymbol({
                    text: String(index + 1),
                    color: "#362044",
                    font: { size: 11, family: "Arial", weight: 'bolder' },
                    yoffset: 10
                });

                const textGraphic = new Graphic({
                    geometry: point,
                    symbol: textSymbol
                });

                tempLineLayer.add(textGraphic);
            }
        });
        map.reorder(tempLineLayer, map.layers.length - 1);
    } else {
         const initialGeometry = groupedFeatures.initial[0].geometry;
         dispatch(setMergedGeometry(initialGeometry));
     }
};

export const createPopupContent = (feature, groupedFeatures, objectIdFieldName) => {
    const objectId = feature.graphic.attributes[objectIdFieldName];
    let message = "This plot does not have adjacent edges";

    Object.entries(groupedFeatures).forEach(([group, features]) => {
        if (features.some(f => f.attributes[objectIdFieldName] === objectId)) {
            if (group === "adjacent") {
                message = "Add to multiple plot list";
            } else if (group === "toMerge") {
                message = "Remove from multiple plot list";
            }
        }
    });

    return `
    <div style="
      color: #362044;
      font-size: 16px;
      font-family: 'Cairo', sans-serif;
      font-weight: 400;
      text-align: center;
      margin: 0;
      padding: 4px 6px;
      line-height: 21px;
      height: 29px;
      min-height: 29px;
      max-height: 29px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      ">
      ${message}
    </div>
  `;
};

export const createCursorIcon = () => {
    if (cursorIconInstance) {
        return cursorIconInstance;
    }

    const cursorIcon = document.createElement("div");
    cursorIcon.className = "plot-cursor-icon";
    cursorIcon.style.position = "absolute";
    cursorIcon.style.fontSize = "18x";
    cursorIcon.style.fontWeight = "bold";
    cursorIcon.style.color = "#341F44";
    cursorIcon.style.pointerEvents = "none";
    cursorIcon.style.display = "none";
    cursorIcon.style.transform = "translate(-50%, -50%)";

    document.body.appendChild(cursorIcon);

    cursorIconInstance = cursorIcon;
    return cursorIcon;
};

export const handlePointerMove = (event, viewer, mapLayer, groupedFeatures, objectIdFieldName, cursorIcon, state) => {
    viewer.hitTest(event).then(response => {
        const result = response.results.find(res => res.graphic.layer === mapLayer);

        if (result) {
            const objectId = result.graphic.attributes[objectIdFieldName];

            const isExcludedGroup = Object.entries(groupedFeatures).some(
                ([group, features]) =>
                    (group === "initial" || group === "filled") &&
                    features.some(f => f.attributes[objectIdFieldName] === objectId)
            );

            if (isExcludedGroup) {
                viewer.closePopup();
                state.isPopupOpen = false;
                state.currentObjectId = null;
                clearTimeout(state.popupTimeout);
                cursorIcon.style.display = "none";
            } else {
                const screenPoint = { x: event.x, y: event.y };
                const mapPoint = viewer.toMap(screenPoint);

                if (objectId !== state.currentObjectId) {
                    viewer.closePopup();
                    state.isPopupOpen = false;
                    clearTimeout(state.popupTimeout);

                    state.currentObjectId = objectId;

                    state.popupTimeout = setTimeout(() => {
                        state.isPopupOpen = true;
                        viewer.popup.dockEnabled = false;
                        viewer.openPopup({
                            location: mapPoint,
                            features: [result.graphic]
                        });
                    }, 350);
                } else if (state.isPopupOpen) {
                    viewer.popup.location = mapPoint;
                }

                let iconText = "";
                if (groupedFeatures["toMerge"]?.some(f => String(f.attributes[objectIdFieldName]) === String(objectId))) {
                    iconText = "−";
                } else if (groupedFeatures["adjacent"]?.some(f => String(f.attributes[objectIdFieldName]) === String(objectId))) {
                    iconText = "+";
                }

                if (iconText) {
                    cursorIcon.textContent = iconText;
                    cursorIcon.style.display = "block";

                    if (window.innerWidth > 1440) {
                        cursorIcon.style.left = `${event.x + 532}px`;
                        cursorIcon.style.top = `${event.y + 90}px`;
                    } else {
                        cursorIcon.style.left = `${event.x + 370}px`;
                        cursorIcon.style.top = `${event.y + 81}px`;
                    }
                } else {
                    cursorIcon.style.display = "none";
                }
            }
        } else {
            clearTimeout(state.popupTimeout);
            viewer.closePopup();
            state.isPopupOpen = false;
            state.currentObjectId = null;
            cursorIcon.style.display = "none";
        }
    });
};

export const applyPlotColor = (mapLayer, groupedFeatures, objectIdFieldName, viewer) => {    
    const originalRenderer = getOriginalRenderer(mapLayer);

    const colors = {
        initial: { type: 'simple-fill', color: '#84B83580', outline: { width: 1, color: '#2E1A47' } },
        adjacent: { type: 'simple-fill', color: '#007086B2', outline: { width: 1, color: '#2E1A47' } },
        toMerge: { type: 'simple-fill', color: '#FDC288B2', outline: { width: 1, color: '#2E1A47' } },
    };

    const expressions = {
        initial: getExpression(objectIdFieldName, groupedFeatures.initial.map(f => f.attributes[objectIdFieldName])),
        adjacent: getExpression(objectIdFieldName, groupedFeatures.adjacent.map(f => f.attributes[objectIdFieldName])),
        toMerge: getExpression(objectIdFieldName, groupedFeatures.toMerge.map(f => f.attributes[objectIdFieldName])),
    };

    const conditions = [];
    if (expressions.initial) conditions.push(`${expressions.initial}, 'Initial'`);
    if (expressions.adjacent) conditions.push(`${expressions.adjacent}, 'Adjacent'`);
    if (expressions.toMerge) conditions.push(`${expressions.toMerge}, 'ToMerge'`);

    const combinedExpression = conditions.length > 0 ? conditions.join(', ') : '';

    if (!mapLayer._originalUniqueValueInfos) {
        mapLayer._originalUniqueValueInfos = Array.isArray(originalRenderer.uniqueValueInfos) ? [...originalRenderer.uniqueValueInfos] : [];
    }

    const uniqueValueInfos = [];

    if (expressions.initial) uniqueValueInfos.push({ value: 'Initial', symbol: colors.initial, label: "Initial plot" });
    if (expressions.adjacent) uniqueValueInfos.push({ value: 'Adjacent', symbol: colors.adjacent, label: "Plots to merge" });
    if (expressions.toMerge) uniqueValueInfos.push({ value: 'ToMerge', symbol: colors.toMerge, label: "Selected plot" });

    if (originalRenderer.type === "unique-value") {
        mapLayer._originalUniqueValueInfos.forEach(original => {
            uniqueValueInfos.push({
                value: original.value,
                symbol: original.symbol,
                label: original.label
            });
        });

        originalRenderer.uniqueValueInfos = [...uniqueValueInfos];
    }

    setRenderer(mapLayer, originalRenderer, null, combinedExpression, true);

    mapLayer.popupTemplate = {
        overwriteActions: true,
        lastEditInfoEnabled: false,
        content: feature => createPopupContent(feature, groupedFeatures, objectIdFieldName)
    };

    excludedObjectIds.clear();
    [...groupedFeatures.initial, ...groupedFeatures.filled].forEach(f =>
        excludedObjectIds.add(f.attributes[objectIdFieldName])
    );

    const cursorIcon = createCursorIcon();
    const state = { currentObjectId: null, isPopupOpen: false, popupTimeout: null };

    if (pointerMoveHandler) {
        pointerMoveHandler.remove();
    }

    pointerMoveHandler = viewer.on("pointer-move", event => {
        handlePointerMove(event, viewer, mapLayer, groupedFeatures, objectIdFieldName, cursorIcon, state);
    });

    viewer.on("pointer-leave", () => {
        cursorIcon.style.display = "none";
    });
};


export function resetPlotStyles(objectIds, mapLayer) {
    if (!mapLayer || !objectIds || objectIds.length === 0) return;

    const originalRenderer = getOriginalRenderer(mapLayer);
    if (originalRenderer) {
        mapLayer.renderer = originalRenderer;
    }

    objectIds.forEach(id => excludedObjectIds.delete(id));

    mapLayer.popupTemplate = null;

    if (pointerMoveHandler) {
        pointerMoveHandler.remove();
        pointerMoveHandler = null;
    }

    const cursorIcon = document.getElementById("custom-cursor");
    if (cursorIcon) {
        cursorIcon.style.display = "none";
    }
}

